import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import DashboardSeparator from "@/components/dashboard/DashboardSeparator";
import NavigationCards from "@/components/dashboard/NavigationCards";
import NewsCarousel from "@/components/dashboard/NewsCarousel";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { KycVerificationBanner } from "@/components/kyc/KycVerificationBanner";
import { BankIdComplementBanner } from "@/components/kyc/BankIdComplementBanner";
import { useIdVerification } from "@/hooks/useIdVerification";
import { KycSuccessDialog } from "@/components/kyc/KycSuccessDialog";
import { useRegistrationRequests } from "@/hooks/useRegistrationRequests";
import { QuickGuideSidePanel } from "@/components/quick-guide/QuickGuideSidePanel";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels } from "@/i18n";
import { AdminFeedbackSidePanel } from "@/components/feedback/AdminFeedbackSidePanel";
import { useUser } from "@/hooks/useUser";
import { AdminStatsBar } from "@/admin/components/dashboard/AdminStatsBar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Car, ChevronRight } from "lucide-react";
import { useNativePlatform } from "@/hooks/useNativePlatform";

import { cn } from "@/lib/utils";
import { DeliveryAddressReminderBanner } from "@/components/dashboard/DeliveryAddressReminderBanner";
import { NativePushTipBanner } from "@/components/dashboard/NativePushTipBanner";
import { DashboardUnreadNotifications } from "@/components/dashboard/DashboardUnreadNotifications";
import { useSupportUnread } from "@/hooks/useSupportUnread";

const Dashboard = () => {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const dashLabels = dashboardLabels(language);
  const {
    showSuccessDialog,
    setShowSuccessDialog,
    isLoadingStatus,
    isApproved: kycApproved,
    isPending: kycPending,
    isDeclined: kycDeclined,
    needsVerification: kycNeedsVerification,
  } = useIdVerification();
  const { userDetails, isLoadingUserDetails } = useUser();

  // Get pending registration requests count for badge indicator (only for admins)
  const isAdmin = user?.type === UserType.Admin;
  const { stats } = useRegistrationRequests(
    1,
    1,
    "pending",
    undefined,
    undefined,
    undefined,
    { enabled: isAdmin }
  );
  const pendingRequestsCount = isAdmin ? stats?.pending?.total || 0 : 0;
  const { data: supportUnread } = useSupportUnread();
  const supportUnreadCount = supportUnread?.count ?? 0;
  // For non-admin users, wait for KYC status to load before showing dashboard
  const shouldShowLoading = user?.type !== UserType.Admin && isLoadingStatus;


  if (shouldShowLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">{dashLabels?.loadingDashboard ?? "Laddar dashboard..."}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {user && user.type === UserType.Admin && (
        <AdminFeedbackSidePanel />
      )}
      <DashboardUnreadNotifications />
      {isNative && <NativePushTipBanner />}
      {user?.type !== UserType.Admin && (
        <>
          <KycVerificationBanner
            isApproved={kycApproved}
            isPending={kycPending}
            isDeclined={kycDeclined}
            needsVerification={kycNeedsVerification}
          />
          <BankIdComplementBanner />
          <DeliveryAddressReminderBanner />
        </>
      )}
      {user?.type === UserType.Admin && (
        <div className="mb-2 w-full">
          <AdminStatsBar />
        </div>
      )}
      <NavigationCards
        pendingRequestsCount={pendingRequestsCount}
        supportUnreadCount={supportUnreadCount}
      />
      {user?.type === UserType.Dealer && (
        <div className="w-full md:w-[85%] md:mx-auto mt-6 relative">
          {(() => {
            const dealerPortalBtnClass = cn(
              "relative w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium text-center transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 overflow-visible",
            );
            const dealerPortalBtnInner = (
              <>
                <Car className="w-5 h-5 shrink-0" />
                <span>
                  {dashLabels?.dealerPortal?.buttonLabel ??
                    dashLabels?.dealerPortal?.title ??
                    "Dealer portal"}
                </span>
                <ChevronRight className="w-4 h-4 shrink-0" />
              </>
            );

            if (isNative) {
              return (
                <button
                  type="button"
                  className={dealerPortalBtnClass}
                  onClick={() => navigate("/dealer-portal")}
                >
                  {dealerPortalBtnInner}
                </button>
              );
            }

            return (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Link to="/dealer-portal" className={dealerPortalBtnClass}>
                    {dealerPortalBtnInner}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="font-bold">
                    {dashLabels?.dealerPortal?.tooltipTitle ??
                      "Exclusive for car dealers"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dashLabels?.dealerPortal?.tooltipDesc ??
                      "Here you manage customer journeys, follow-ups and access to tools adapted for car dealers."}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })()}
        </div>
      )}
      <DashboardSeparator />
      <NewsCarousel />
      {user?.type !== UserType.Admin && (
        <>
          <KycSuccessDialog
            isOpen={showSuccessDialog}
            onClose={() => setShowSuccessDialog(false)}
          />
          <QuickGuideSidePanel
            showQuickGuide={userDetails?.showQuickGuide ?? false}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
